import React, { useState, useCallback, useRef, useEffect } from 'react';
import { ARCHETYPES } from '../config/archetypes';
import ResourceViewer from './ResourceViewer';

const API_BASE = process.env.REACT_APP_API_URL;

// ─── Shared colour helpers ────────────────────────────────────────────────────

const SCORE_COLORS = {
  high: { ring: 'border-[#3fb950]', text: 'text-[#3fb950]', bg: 'bg-[#3fb950]/10' },
  mid:  { ring: 'border-[#d29922]', text: 'text-[#d29922]', bg: 'bg-[#d29922]/10' },
  low:  { ring: 'border-[#f85149]', text: 'text-[#f85149]', bg: 'bg-[#f85149]/10' },
};

function getScoreColor(score, max = 6) {
  const pct = score / max;
  if (pct >= 0.75) return SCORE_COLORS.high;
  if (pct >= 0.5)  return SCORE_COLORS.mid;
  return SCORE_COLORS.low;
}

// ─── Score dials ─────────────────────────────────────────────────────────────

function ScoreDial({ score, max = 6 }) {
  const colors = getScoreColor(score, max);
  return (
    <div className={`flex items-center justify-center w-20 h-20 rounded-full border-4 ${colors.ring} ${colors.bg} score-reveal`}>
      <div className="text-center">
        <span className={`text-2xl font-bold ${colors.text}`}>{score}</span>
        <span className="text-[#8b949e] text-xs block">/{max}</span>
      </div>
    </div>
  );
}

function OutlineScoreDial({ score, max = 4 }) {
  return (
    <div
      className="flex items-center justify-center w-20 h-20 rounded-full border-4 score-reveal"
      style={{ borderColor: '#64748b', backgroundColor: 'rgba(100,116,139,0.10)' }}
    >
      <div className="text-center">
        <span className="text-2xl font-bold" style={{ color: '#64748b' }}>{score}</span>
        <span className="text-[#8b949e] text-xs block">/{max}</span>
      </div>
    </div>
  );
}

// ─── Word count helper ────────────────────────────────────────────────────────

function wordCount(text) {
  if (!text?.trim()) return 0;
  return text.trim().split(/\s+/).length;
}

// ─── Debug modal (request / response) ────────────────────────────────────────

function DebugModal({ attemptId, getToken, onClose }) {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);
  const [tab, setTab]         = useState('prompts'); // 'prompts' | 'responses'

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const token = await getToken();
        const res   = await fetch(`${API_BASE}/api/attempt-logs/${attemptId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
        const json = await res.json();
        if (!cancelled) setData(json);
      } catch (e) {
        if (!cancelled) setError(e.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [attemptId, getToken]);

  const tabs = [
    { id: 'prompts',   label: 'Prompts' },
    { id: 'responses', label: 'Responses' },
  ];

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-3xl max-h-[85vh] mx-4 flex flex-col bg-[#0d1117] border border-[#30363d] rounded-xl shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-[#30363d] flex-shrink-0">
          <div className="flex items-center gap-3">
            <span className="text-[#8b949e] text-xs font-mono font-semibold uppercase tracking-wider">
              Request &amp; Response
            </span>
            {data && (
              <span className="text-[10px] font-mono text-[#484f58]">
                {data.provider} · {data.model} · {data.latencyMs}ms · {(data.inputTokens ?? 0) + (data.outputTokens ?? 0)} tokens
              </span>
            )}
          </div>
          <button
            onClick={onClose}
            className="text-[#484f58] hover:text-[#e6edf3] transition-colors text-lg leading-none"
          >
            ×
          </button>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 px-4 py-2 border-b border-[#30363d] flex-shrink-0">
          {tabs.map(({ id, label }) => (
            <button
              key={id}
              onClick={() => setTab(id)}
              className={`px-3 py-1 rounded text-[10px] font-mono transition-colors ${
                tab === id
                  ? 'bg-[#161b22] text-[#e6edf3] border border-[#30363d]'
                  : 'text-[#484f58] hover:text-[#8b949e]'
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4">
          {loading && (
            <div className="flex items-center gap-2 text-[#8b949e] text-xs font-mono py-8 justify-center">
              <div className="w-1.5 h-1.5 rounded-full bg-[#58a6ff] animate-pulse" />
              Fetching logs…
            </div>
          )}
          {error && (
            <p className="text-[#f85149] text-xs font-mono">{error}</p>
          )}
          {data && tab === 'prompts' && (
            <>
              <DebugSection label="System Prompt" text={data.promptPayload?.mark?.system} />
              <DebugSection label="User Prompt — Mark Call" text={data.promptPayload?.mark?.user} />
              {data.promptPayload?.breakdown?.user && (
                <DebugSection label="User Prompt — Breakdown Call" text={data.promptPayload.breakdown.user} />
              )}
            </>
          )}
          {data && tab === 'responses' && (
            <>
              <DebugSection label="Mark Response" text={JSON.stringify(data.responsePayload?.mark, null, 2)} />
              {data.responsePayload?.breakdown && (
                <DebugSection label="Breakdown Response" text={JSON.stringify(data.responsePayload.breakdown, null, 2)} />
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function DebugSection({ label, text }) {
  if (!text) return null;
  return (
    <div>
      <p className="text-[#8b949e] text-[10px] font-semibold uppercase tracking-wider mb-1.5">{label}</p>
      <pre className="text-[#e6edf3] text-[10px] font-mono leading-relaxed bg-[#161b22] border border-[#30363d] rounded-lg p-3 whitespace-pre-wrap break-words overflow-x-auto max-h-64">
        {text}
      </pre>
    </div>
  );
}

// ─── Justification + debug footer (shared across all archetypes) ──────────────

function FeedbackFooter({ feedback, getToken }) {
  const [justOpen, setJustOpen]     = useState(false);
  const [debugOpen, setDebugOpen]   = useState(false);
  const toggleJust = useCallback(() => setJustOpen((v) => !v), []);
  const openDebug  = useCallback(() => setDebugOpen(true), []);
  const closeDebug = useCallback(() => setDebugOpen(false), []);

  const hasJustification = !!feedback?.justification;
  const hasAttemptId     = !!feedback?.attemptId;

  if (!hasJustification && !hasAttemptId) return null;

  return (
    <>
      <div className="border-t border-[#30363d] pt-3 space-y-2">
        {hasJustification && (
          <div>
            <button
              onClick={toggleJust}
              className="flex items-center gap-1.5 text-[10px] font-mono text-[#484f58] hover:text-[#8b949e] transition-colors group"
            >
              <span className={`transition-transform ${justOpen ? 'rotate-90' : ''}`}>▶</span>
              <span className="group-hover:underline underline-offset-2">
                {justOpen ? 'Hide justification' : 'View justification'}
              </span>
            </button>
            {justOpen && (
              <div className="mt-2 rounded-lg border border-[#30363d] bg-[#161b22] px-3 py-2.5">
                <p className="text-[#8b949e] text-[10px] font-mono leading-relaxed whitespace-pre-wrap">
                  {feedback.justification}
                </p>
              </div>
            )}
          </div>
        )}

        {hasAttemptId && (
          <button
            onClick={openDebug}
            className="flex items-center gap-1.5 text-[10px] font-mono text-[#484f58] hover:text-[#8b949e] transition-colors group"
          >
            <span>⎘</span>
            <span className="group-hover:underline underline-offset-2">View request &amp; response</span>
          </button>
        )}
      </div>

      {debugOpen && hasAttemptId && (
        <DebugModal
          attemptId={feedback.attemptId}
          getToken={getToken}
          onClose={closeDebug}
        />
      )}
    </>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

// ─── MCQ Feedback Panel ───────────────────────────────────────────────────────

function McqFeedbackPanel({ mcqResult }) {
  const { selected, options, marksAwarded, marksTotal } = mcqResult;
  const isFullMark = marksAwarded === marksTotal;
  const scoreColor = isFullMark ? SCORE_COLORS.high : marksAwarded > 0 ? SCORE_COLORS.mid : SCORE_COLORS.low;

  return (
    <aside className="w-full lg:w-96 h-full lg:h-screen bg-[#0d1117] border-l border-[#30363d] flex flex-col">
      {/* Status bar */}
      <div className="px-4 py-3 border-b border-[#30363d] flex items-center gap-2 flex-shrink-0">
        <div className={`w-2 h-2 rounded-full flex-shrink-0 ${scoreColor.bg.replace('bg-', 'bg-').replace('/10','')}`}
             style={{ backgroundColor: isFullMark ? '#3fb950' : marksAwarded > 0 ? '#d29922' : '#f85149' }} />
        <span className={`text-xs font-semibold tracking-widest uppercase ${scoreColor.text}`}>
          {isFullMark ? 'Correct' : marksAwarded > 0 ? 'Partially Correct' : 'Incorrect'}
        </span>
      </div>

      <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3">
        {/* Score */}
        <div className="flex items-center justify-between">
          <span className="text-[#8b949e] text-xs">Score</span>
          <div className={`flex items-center gap-1.5 px-3 py-1 rounded-full border ${scoreColor.ring} ${scoreColor.bg}`}>
            <span className={`text-sm font-bold font-mono ${scoreColor.text}`}>{marksAwarded}</span>
            <span className="text-[#8b949e] text-xs">/{marksTotal}</span>
          </div>
        </div>

        <div className="border-t border-[#21262d]" />

        {/* Per-option feedback */}
        {options.map((opt) => {
          const wasSelected = selected.includes(opt.label);
          let border = 'border-[#21262d]';
          let labelBg = 'bg-[#161b22]';
          let labelText = 'text-[#484f58]';
          let icon = null;

          if (wasSelected && opt.is_correct) {
            border = 'border-[#3fb950]/40';
            labelBg = 'bg-[#3fb950]/10';
            labelText = 'text-[#3fb950]';
            icon = (
              <svg className="w-3.5 h-3.5 text-[#3fb950] flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
              </svg>
            );
          } else if (wasSelected && !opt.is_correct) {
            border = 'border-[#f85149]/40';
            labelBg = 'bg-[#f85149]/10';
            labelText = 'text-[#f85149]';
            icon = (
              <svg className="w-3.5 h-3.5 text-[#f85149] flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            );
          } else if (!wasSelected && opt.is_correct && marksTotal > 1) {
            border = 'border-[#d29922]/40';
            labelBg = 'bg-[#d29922]/10';
            labelText = 'text-[#d29922]';
            icon = (
              <svg className="w-3.5 h-3.5 text-[#d29922] flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            );
          }

          const showFeedback = wasSelected || opt.is_correct;

          return (
            <div key={opt.label} className={`rounded-lg border ${border} p-3 space-y-1.5`}>
              <div className="flex items-start gap-2">
                <span className={`flex-shrink-0 w-5 h-5 rounded text-[10px] font-mono font-semibold flex items-center justify-center ${labelBg} ${labelText}`}>
                  {opt.label}
                </span>
                <p className="text-[#e6edf3] text-xs leading-snug flex-1">{opt.text}</p>
                {icon}
              </div>
              {showFeedback && (
                <p className="text-[#8b949e] text-xs leading-relaxed pl-7">{opt.feedback}</p>
              )}
            </div>
          );
        })}
      </div>
    </aside>
  );
}

export default function FeedbackTerminal({
  feedbacks,
  errors,
  loadings,
  loadingBreakdowns,
  activeProvider,
  onClear,
  markType,
  archetype,
  steps,
  timerElapsed,
  getToken,
  resource = null,
  mcqResult = null,
  questionMarks = null,
}) {
  const feedback         = feedbacks[activeProvider];
  const error            = errors[activeProvider];
  const isLoading        = loadings[activeProvider];
  const loadingBreakdown = loadingBreakdowns?.[activeProvider] ?? false;

  const [peekMode, setPeekMode] = useState(false);

  // Reset peek tab whenever new feedback arrives (so the result has focus).
  useEffect(() => {
    setPeekMode(false);
  }, [feedbacks]);

  // When marking starts and there is a figure, switch focus to the resource
  // tab so the student can re-read their answer alongside the figure while
  // waiting for the marker. The effect on `feedbacks` above flips back to
  // the feedback tab when the result arrives.
  useEffect(() => {
    if (isLoading && resource) setPeekMode(true);
  }, [isLoading, resource]);

  // Status bar
  const dotColor  = isLoading ? 'bg-[#d29922] animate-pulse'
    : feedback ? 'bg-[#3fb950]'
    : error    ? 'bg-[#f85149]'
    : 'bg-[#30363d]';
  const textColor = isLoading ? 'text-[#d29922]'
    : feedback ? 'text-[#3fb950]'
    : error    ? 'text-[#f85149]'
    : 'text-[#8b949e]';
  const statusLabel = isLoading ? 'Scoring…' : loadingBreakdown ? 'Analysing…' : feedback ? 'Marked' : error ? 'Error' : 'Examiner Feedback';
  const dotColor2   = loadingBreakdown ? 'bg-[#d29922] animate-pulse' : dotColor;
  const textColor2  = loadingBreakdown ? 'text-[#d29922]' : textColor;

  const scrollRef = useRef(null);

  // Scroll to top when marking starts or feedback arrives
  useEffect(() => {
    if (isLoading || feedback) {
      scrollRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [isLoading, feedback]);

  if (mcqResult) return <McqFeedbackPanel mcqResult={mcqResult} />;

  return (
    <aside className="w-full lg:w-96 h-full lg:h-screen bg-[#0d1117] border-l border-[#30363d] flex flex-col">

      {/* ── Status bar ──────────────────────────────────────────────────── */}
      <div className="px-4 py-3 border-b border-[#30363d] flex items-center justify-between flex-shrink-0">
        <div className="flex items-center gap-2 min-w-0">
          <div className={`w-2 h-2 rounded-full flex-shrink-0 ${dotColor2}`} />
          <span className={`text-xs font-semibold tracking-widest uppercase ${textColor2}`}>
            {statusLabel}
          </span>
          <span className="text-[#484f58] text-[10px] font-mono truncate">
            · {activeProvider === 'claude' ? 'Claude Sonnet 4.6' : 'Gemini 2.5 Flash'}
          </span>
        </div>
        {(feedback || error) && (
          <button
            onClick={onClear}
            className="text-[10px] px-2 py-1 rounded border border-[#30363d] text-[#8b949e] hover:border-[#f85149]/50 hover:text-[#f85149] transition-colors flex-shrink-0 ml-2"
          >
            Clear & Try Again
          </button>
        )}
      </div>

      {/* ── Peek tab bar (only when a resource is available) ─────────────── */}
      {resource && (
        <div className="flex flex-shrink-0 border-b border-[#30363d] bg-[#0d1117]">
          <button
            onClick={() => setPeekMode(false)}
            className={`flex-1 py-2 text-xs font-mono transition-colors ${
              !peekMode
                ? 'text-[#58a6ff] border-b-2 border-[#58a6ff] bg-[#58a6ff]/5'
                : 'text-[#8b949e] hover:text-[#e6edf3]'
            }`}
          >
            Feedback
          </button>
          <button
            onClick={() => setPeekMode(true)}
            className={`flex-1 py-2 text-xs font-mono transition-colors ${
              peekMode
                ? 'text-[#58a6ff] border-b-2 border-[#58a6ff] bg-[#58a6ff]/5'
                : 'text-[#8b949e] hover:text-[#e6edf3]'
            }`}
          >
            Resource
          </button>
        </div>
      )}

      {/* ── Marking header (always visible while loading, regardless of tab) ── */}
      {isLoading && (() => {
        const hasCardSteps = ['outline_4', 'data_6', 'assess_6', 'scaffold_9', 'essay_20'].includes(archetype) && steps;
        if (!hasCardSteps) return null;

        const cardLabels = ARCHETYPES[archetype]?.cards?.map((c) => c.label) ?? [];
        let answerContent;
        if (archetype === 'scaffold_9') {
          const p1 = cardLabels.slice(0, 4).map((lbl) => steps[lbl]?.trim()).filter(Boolean).join(' ');
          const p2 = cardLabels.slice(4, 8).map((lbl) => steps[lbl]?.trim()).filter(Boolean).join(' ');
          const p3 = steps[cardLabels[8]]?.trim() || '';
          answerContent = (
            <div className="space-y-3">
              {p1 && (
                <div>
                  <span className="text-[9px] font-mono text-[#8b949e] uppercase tracking-widest">Paragraph 1 — Point 1</span>
                  <p className="text-[#e6edf3] text-xs leading-relaxed mt-0.5">{p1}</p>
                </div>
              )}
              {p2 && (
                <div>
                  <span className="text-[9px] font-mono text-[#8b949e] uppercase tracking-widest">Paragraph 2 — Point 2</span>
                  <p className="text-[#e6edf3] text-xs leading-relaxed mt-0.5">{p2}</p>
                </div>
              )}
              {p3 && (
                <div>
                  <span className="text-[9px] font-mono text-[#8b949e] uppercase tracking-widest">Paragraph 3 — Conclusion</span>
                  <p className="text-[#e6edf3] text-xs leading-relaxed mt-0.5">{p3}</p>
                </div>
              )}
            </div>
          );
        } else {
          const answerText = cardLabels.map((lbl) => steps[lbl]?.trim()).filter(Boolean).join(' ');
          answerContent = <p className="text-[#e6edf3] text-xs leading-relaxed">{answerText}</p>;
        }
        return (
          <div className="flex-shrink-0 px-4 py-4 border-b border-[#30363d] bg-[#0d1117]">
            <p className="text-[#8b949e] text-[10px] font-semibold uppercase tracking-wider mb-2">
              Your Answer
            </p>
            {answerContent}
            <div className="flex gap-1 mt-3">
              {[0, 1, 2].map((i) => (
                <div
                  key={i}
                  className="w-1.5 h-1.5 rounded-full bg-[#8b5cf6]"
                  style={{ animation: `pulse 1.2s ease-in-out ${i * 0.2}s infinite` }}
                />
              ))}
            </div>
          </div>
        );
      })()}

      {/* ── Peek: show resource viewer ───────────────────────────────────── */}
      {peekMode && resource ? (
        <div className="flex-1 min-h-0 overflow-hidden">
          <ResourceViewer resource={resource} />
        </div>
      ) : (
      <div ref={scrollRef} className="flex-1 overflow-y-auto">

        {/* Empty */}
        {!feedback && !error && !isLoading && (
          <div className="flex-1 flex items-center justify-center h-full">
            <div className="text-center px-6 space-y-3">
              <div className="text-4xl opacity-10">▶</div>
              <p className="text-[#8b949e] text-xs leading-relaxed">
                Complete your answer and click{' '}
                <span className="text-[#58a6ff]">Mark My Answer</span> to receive AQA-style feedback.
              </p>
            </div>
          </div>
        )}

        {/* Loading body — header above shows the answer; body shows the spinner */}
        {isLoading && (
          <div className="flex items-center justify-center h-full py-8">
            <div className="text-center space-y-4">
              <div className="flex gap-1 justify-center">
                {[0, 1, 2].map((i) => (
                  <div
                    key={i}
                    className="w-2 h-2 bg-[#58a6ff] rounded-full animate-bounce"
                    style={{ animationDelay: `${i * 0.15}s` }}
                  />
                ))}
              </div>
              <p className="text-[#8b949e] text-xs">Consulting the mark scheme…</p>
            </div>
          </div>
        )}

        {/* Error */}
        {error && (
          <div className="px-4 py-6 flex flex-col gap-4">
            <div className="rounded-lg border border-[#f85149]/30 bg-[#f85149]/10 p-4">
              <p className="text-[#f85149] text-xs font-mono leading-relaxed">{error}</p>
            </div>
          </div>
        )}

        {/* ── OUTLINE_4 FEEDBACK ──────────────────────────────────────────── */}
        {feedback && !isLoading && archetype === 'outline_4' && (() => {
          const cards      = ARCHETYPES.outline_4.cards;
          const cardLabels = ['POINT 1', 'DEVELOPMENT 1', 'POINT 2', 'DEVELOPMENT 2'];
          const cardFb     = feedback.card_feedback ?? {};

          const warnings = cards
            .map((card) => {
              const wc = wordCount(steps?.[card.label]);
              return wc > card.maxWords ? { label: card.label, wc, max: card.maxWords } : null;
            })
            .filter(Boolean);

          return (
            <div className="px-4 py-5 space-y-5">
              {steps && (
                <div>
                  <p className="text-[#8b949e] text-[10px] font-semibold uppercase tracking-wider mb-2">
                    Your Answer
                  </p>
                  <p className="text-[#e6edf3] text-xs leading-relaxed">
                    {['POINT 1', 'DEVELOPMENT 1', 'POINT 2', 'DEVELOPMENT 2']
                      .map((lbl) => steps[lbl]?.trim())
                      .filter(Boolean)
                      .join(' ')}
                  </p>
                  {timerElapsed > 0 && (() => {
                    const suggested = (ARCHETYPES[archetype]?.marks ?? 4) * 60;
                    const pct       = Math.round((timerElapsed / suggested) * 100);
                    const mins      = Math.floor(timerElapsed / 60);
                    const secs      = timerElapsed % 60;
                    const timeStr   = `${mins}m ${secs.toString().padStart(2, '0')}s`;
                    const sugStr    = `${Math.floor(suggested / 60)}m 00s`;
                    const color     = pct > 200 ? 'text-[#f85149]' : pct > 100 ? 'text-[#d29922]' : 'text-[#484f58]';
                    return (
                      <p className={`text-[10px] font-mono mt-2 ${color}`}>
                        ⏱ {timeStr} — {pct}% of suggested exam time ({sugStr})
                      </p>
                    );
                  })()}
                  <div className="mt-4 border-t border-[#30363d]" />
                </div>
              )}

              <div className="flex items-center gap-4">
                <OutlineScoreDial score={feedback.total ?? 0} max={4} />
                <div>
                  <div className="text-sm font-semibold text-slate-400">{feedback.total ?? 0}/4 marks</div>
                  <div className="text-[#8b949e] text-xs mt-0.5">Point-mark · AO1</div>
                </div>
              </div>

              <div>
                <p className="text-[#8b949e] text-[10px] font-semibold uppercase tracking-wider mb-2">
                  Card Breakdown
                </p>
                <div className="space-y-2">
                  {cardLabels.map((lbl) => {
                    const cf      = cardFb[lbl] ?? {};
                    const awarded = cf.marks ?? 0;
                    const comment = cf.comment ?? '';
                    const minImp  = cf.minimal_improvement ?? null;
                    return (
                      <div key={lbl} className="rounded-lg border border-[#30363d] bg-[#161b22] px-3 py-2">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-[10px] font-mono text-slate-400 font-semibold">{lbl}</span>
                          <span className={`text-[10px] font-mono font-bold px-1.5 py-0.5 rounded ${
                            awarded === 1 ? 'text-[#3fb950] bg-[#3fb950]/10' : 'text-[#f85149] bg-[#f85149]/10'
                          }`}>
                            [{awarded}/1]
                          </span>
                        </div>
                        {comment && <p className="text-[#8b949e] text-xs leading-relaxed">{comment}</p>}
                        {minImp && (
                          <p className="text-[#d29922] text-[10px] font-mono mt-1.5 leading-relaxed">✏ {minImp}</p>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              {warnings.length > 0 && (
                <div className="space-y-1.5">
                  {warnings.map(({ label, wc, max }) => (
                    <div key={label} className="text-[10px] font-mono text-[#f85149] bg-[#f85149]/5 border border-[#f85149]/20 rounded px-2.5 py-1.5">
                      ⚠ {label} over max ({wc}/{max} words)
                    </div>
                  ))}
                </div>
              )}

              {feedback.examiner_note && (
                <div className="rounded-lg border border-[#30363d] bg-[#161b22] px-3 py-2.5">
                  <div className="text-[10px] font-mono leading-relaxed">
                    <span className="text-[#8b949e]">user@tutor:~$</span>{' '}
                    <span className="text-[#e6edf3]">examiner_note</span>
                    <br />
                    <span className="text-[#8b949e] mt-1 block italic">&gt; {feedback.examiner_note}</span>
                  </div>
                </div>
              )}

              <FeedbackFooter feedback={feedback} getToken={getToken} />

              <div className="pb-4" />
            </div>
          );
        })()}

        {/* ── ASSESS_6 FEEDBACK ───────────────────────────────────────────── */}
        {feedback && !isLoading && archetype === 'assess_6' && (() => {
          const cards      = ARCHETYPES.assess_6.cards;
          const cardLabels = cards.map((c) => c.label);
          const cardFb     = feedback.card_feedback ?? {};
          const level      = feedback.level ?? 1;
          const amberHex   = '#f59e0b';

          const warnings = cards
            .map((card) => {
              const wc = wordCount(steps?.[card.label]);
              return wc > card.maxWords ? { label: card.label, wc, max: card.maxWords } : null;
            })
            .filter(Boolean);

          const level2Blocker = (() => {
            if (level === 2) return null;
            const cf = cardFb;
            if (cf['ASSESSED POINT 1 (AO2)']?.has_judgment === false) return 'Add an explicit judgment phrase ("This suggests…") to Assessed Point 1.';
            if (cf['ASSESSED POINT 2 (AO2)']?.has_judgment === false) return 'Add an explicit judgment phrase ("This suggests…") to Assessed Point 2.';
            if (cf['OVERALL JUDGMENT']?.answers_question === false)    return 'Rewrite the Overall Judgment to directly answer the question.';
            if (cf['CONTEXT (AO1)']?.marks < 2)                       return 'Strengthen the Context with accurate geographical terminology.';
            return feedback.level_reason ?? null;
          })();

          return (
            <div className="px-4 py-5 space-y-5">
              {steps && (
                <div>
                  <p className="text-[#8b949e] text-[10px] font-semibold uppercase tracking-wider mb-2">Your Answer</p>
                  <p className="text-[#e6edf3] text-xs leading-relaxed">
                    {cardLabels.map((lbl) => steps[lbl]?.trim()).filter(Boolean).join(' ')}
                  </p>
                  {timerElapsed > 0 && (() => {
                    const suggested = 6 * 60;
                    const pct       = Math.round((timerElapsed / suggested) * 100);
                    const mins      = Math.floor(timerElapsed / 60);
                    const secs      = timerElapsed % 60;
                    const timeStr   = `${mins}m ${secs.toString().padStart(2, '0')}s`;
                    const sugStr    = `${Math.floor(suggested / 60)}m 00s`;
                    const color     = pct > 200 ? 'text-[#f85149]' : pct > 100 ? 'text-[#d29922]' : 'text-[#484f58]';
                    return (
                      <p className={`text-[10px] font-mono mt-2 ${color}`}>
                        ⏱ {timeStr} — {pct}% of suggested exam time ({sugStr})
                      </p>
                    );
                  })()}
                  <div className="mt-4 border-t border-[#30363d]" />
                </div>
              )}

              <div className="flex items-center gap-4">
                <div
                  className="flex items-center justify-center w-20 h-20 rounded-full border-4 score-reveal flex-shrink-0"
                  style={{ borderColor: amberHex, backgroundColor: 'rgba(245,158,11,0.10)' }}
                >
                  <div className="text-center">
                    <span className="text-2xl font-bold" style={{ color: amberHex }}>{feedback.total ?? 0}</span>
                    <span className="text-[#8b949e] text-xs block">/6</span>
                  </div>
                </div>
                <div>
                  <div className="text-sm font-semibold" style={{ color: amberHex }}>
                    {feedback.total ?? 0}/6 marks
                  </div>
                  <div className="text-[#8b949e] text-xs mt-0.5 font-mono">
                    AO1 {feedback.ao1_marks ?? 0}/2 &nbsp; AO2 {feedback.ao2_marks ?? 0}/4
                  </div>
                </div>
              </div>

              <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 px-3 py-2.5">
                <div className="text-xs font-bold font-mono tracking-widest" style={{ color: amberHex }}>
                  LEVEL {level}
                </div>
                {feedback.level_reason && (
                  <p className="text-[#8b949e] text-[10px] mt-1 leading-relaxed italic">{feedback.level_reason}</p>
                )}
              </div>

              <div>
                <p className="text-[#8b949e] text-[10px] font-semibold uppercase tracking-wider mb-2">Card Breakdown</p>
                <div className="space-y-2">
                  {cardLabels.map((lbl) => {
                    const cf         = cardFb[lbl] ?? {};
                    const isContext  = lbl === 'CONTEXT (AO1)';
                    const isJudgment = lbl === 'OVERALL JUDGMENT';
                    const maxMarks   = isContext ? 2 : isJudgment ? 0 : 2;
                    const awarded    = cf.marks ?? 0;
                    const comment    = cf.comment ?? '';
                    const minImp     = cf.minimal_improvement ?? null;

                    return (
                      <div key={lbl} className="rounded-lg border border-[#30363d] bg-[#161b22] px-3 py-2">
                        <div className="flex items-center justify-between mb-1 gap-2 flex-wrap">
                          <span className="text-[10px] font-mono text-amber-400/80 font-semibold">{lbl}</span>
                          <div className="flex items-center gap-1.5">
                            {lbl.includes('(AO2)') && (
                              <span className={`text-[10px] font-mono px-1.5 py-0.5 rounded ${cf.has_judgment ? 'text-[#3fb950] bg-[#3fb950]/10' : 'text-[#f85149] bg-[#f85149]/10'}`}>
                                {cf.has_judgment ? '[✓ judgment]' : '[✗ judgment]'}
                              </span>
                            )}
                            {isJudgment && (
                              <span className={`text-[10px] font-mono px-1.5 py-0.5 rounded ${cf.answers_question ? 'text-[#3fb950] bg-[#3fb950]/10' : 'text-[#f85149] bg-[#f85149]/10'}`}>
                                {cf.answers_question ? '[✓ answers question]' : '[✗ answers question]'}
                              </span>
                            )}
                            {!isJudgment && (
                              <span className={`text-[10px] font-mono font-bold px-1.5 py-0.5 rounded ${awarded === maxMarks ? 'text-[#3fb950] bg-[#3fb950]/10' : 'text-[#f85149] bg-[#f85149]/10'}`}>
                                [{awarded}/{maxMarks}]
                              </span>
                            )}
                          </div>
                        </div>
                        {comment && <p className="text-[#8b949e] text-xs leading-relaxed">{comment}</p>}
                        {minImp && (
                          <p className="text-[#d29922] text-[10px] font-mono mt-1.5 leading-relaxed">✏ {minImp}</p>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              {feedback.ao_balance_note && (
                <div className="text-[10px] font-mono text-[#d29922] bg-[#d29922]/5 border border-[#d29922]/20 rounded px-2.5 py-1.5">
                  ⚠ {feedback.ao_balance_note}
                </div>
              )}

              {level === 1 && level2Blocker && (
                <div className="text-[10px] font-mono bg-amber-500/5 border border-amber-500/20 rounded px-2.5 py-1.5 leading-relaxed" style={{ color: amberHex }}>
                  ▶ To reach Level 2: {level2Blocker}
                </div>
              )}

              {warnings.length > 0 && (
                <div className="space-y-1.5">
                  {warnings.map(({ label, wc, max }) => (
                    <div key={label} className="text-[10px] font-mono text-[#f85149] bg-[#f85149]/5 border border-[#f85149]/20 rounded px-2.5 py-1.5">
                      ⚠ {label} over max ({wc}/{max} words)
                    </div>
                  ))}
                </div>
              )}

              {feedback.examiner_note && (
                <div className="rounded-lg border border-[#30363d] bg-[#161b22] px-3 py-2.5">
                  <div className="text-[10px] font-mono leading-relaxed">
                    <span className="text-[#8b949e]">user@tutor:~$</span>{' '}
                    <span className="text-[#e6edf3]">examiner_note</span>
                    <br />
                    <span className="text-[#8b949e] mt-1 block italic">&gt; {feedback.examiner_note}</span>
                  </div>
                </div>
              )}

              <FeedbackFooter feedback={feedback} getToken={getToken} />

              <div className="pb-4" />
            </div>
          );
        })()}

        {/* ── SCAFFOLD 9 FEEDBACK ──────────────────────────────────────────── */}
        {feedback && !isLoading && archetype === 'scaffold_9' && (() => {
          const violetHex  = '#8b5cf6';
          const level      = feedback.level ?? feedback.awarded_level ?? 1;
          const total      = feedback.total ?? feedback.awarded_mark ?? 0;
          const cardFb     = feedback.card_feedback ?? {};
          const paragraphs = ['Paragraph 1', 'Paragraph 2', 'Paragraph 3'];
          const phaseNames = {
            'Paragraph 1': 'Point 1',
            'Paragraph 2': 'Point 2',
            'Paragraph 3': 'Conclusion',
          };

          const levelColors = {
            1: { border: 'border-[#f85149]/30', bg: 'bg-[#f85149]/5', text: 'text-[#f85149]' },
            2: { border: 'border-[#d29922]/30', bg: 'bg-[#d29922]/5', text: 'text-[#d29922]' },
            3: { border: 'border-[#3fb950]/30', bg: 'bg-[#3fb950]/5', text: 'text-[#3fb950]' },
          };
          const lc = levelColors[level] ?? levelColors[1];

          return (
            <div className="px-4 py-5 space-y-5">
              {/* Your Answer */}
              {steps && (() => {
                const cardLabels = ARCHETYPES.scaffold_9?.cards?.map((c) => c.label) ?? [];
                const answerText = cardLabels.map((lbl) => steps[lbl]?.trim()).filter(Boolean).join(' ');
                if (!answerText) return null;
                return (
                  <div>
                    <p className="text-[#8b949e] text-[10px] font-semibold uppercase tracking-wider mb-2">Your Answer</p>
                    <p className="text-[#e6edf3] text-xs leading-relaxed">{answerText}</p>
                    {timerElapsed > 0 && (() => {
                      const suggested = 9 * 60;
                      const pct  = Math.round((timerElapsed / suggested) * 100);
                      const mins = Math.floor(timerElapsed / 60);
                      const secs = timerElapsed % 60;
                      const col  = pct > 200 ? 'text-[#f85149]' : pct > 100 ? 'text-[#d29922]' : 'text-[#484f58]';
                      return (
                        <p className={`text-[10px] font-mono mt-2 ${col}`}>
                          ⏱ {mins}m {secs.toString().padStart(2, '0')}s — {pct}% of suggested exam time (9m 00s)
                        </p>
                      );
                    })()}
                    <div className="mt-4 border-t border-[#30363d]" />
                  </div>
                );
              })()}

              {/* Score dial + AO breakdown */}
              <div className="flex items-center gap-4">
                <div
                  className="flex items-center justify-center w-20 h-20 rounded-full border-4 score-reveal flex-shrink-0"
                  style={{ borderColor: violetHex, backgroundColor: 'rgba(139,92,246,0.10)' }}
                >
                  <div className="text-center">
                    <span className="text-2xl font-bold" style={{ color: violetHex }}>{total}</span>
                    <span className="text-[#8b949e] text-xs block">/9</span>
                  </div>
                </div>
                <div>
                  <div className="text-sm font-semibold" style={{ color: violetHex }}>
                    {total}/9 marks
                  </div>
                  <div className="text-[#8b949e] text-xs mt-0.5 font-mono">
                    AO1 {loadingBreakdown ? '…' : (feedback.ao1_marks ?? '?')}/4 &nbsp; AO2 {loadingBreakdown ? '…' : (feedback.ao2_marks ?? '?')}/5
                  </div>
                </div>
              </div>

              {/* Level badge */}
              <div className={`rounded-lg border ${lc.border} ${lc.bg} px-3 py-2.5`}>
                <div className={`text-xs font-bold font-mono tracking-widest ${lc.text}`}>
                  LEVEL {level}
                </div>
                {feedback.justification && (
                  <p className="text-[#8b949e] text-[10px] mt-1 leading-relaxed italic">
                    {feedback.justification}
                  </p>
                )}
              </div>

              {/* Per-paragraph breakdown */}
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <p className="text-[#8b949e] text-[10px] font-semibold uppercase tracking-wider">Paragraph Breakdown</p>
                  {loadingBreakdown && (
                    <div className="flex gap-0.5">
                      {[0, 1, 2].map((i) => (
                        <div
                          key={i}
                          className="w-1 h-1 rounded-full bg-[#8b5cf6]"
                          style={{ animation: `pulse 1.2s ease-in-out ${i * 0.2}s infinite` }}
                        />
                      ))}
                    </div>
                  )}
                </div>
                {loadingBreakdown ? (
                  <div className="space-y-2">
                    {paragraphs.map((para) => (
                      <div key={para} className="rounded-lg border border-[#30363d] bg-[#161b22] px-3 py-2">
                        <div className="flex items-center justify-between mb-1 gap-2">
                          <span className="text-[10px] font-mono font-semibold" style={{ color: violetHex }}>{para}</span>
                          <span className="text-[10px] font-mono text-[#8b949e]">{phaseNames[para]}</span>
                        </div>
                        <div className="h-2 w-3/4 bg-[#30363d] rounded animate-pulse mt-1" />
                      </div>
                    ))}
                  </div>
                ) : feedback.breakdownError ? (
                  <div className="text-[10px] font-mono text-[#f85149] bg-[#f85149]/5 border border-[#f85149]/20 rounded px-2.5 py-1.5">
                    ⚠ Paragraph analysis unavailable. Score above is still valid.
                  </div>
                ) : (
                  <div className="space-y-2">
                    {paragraphs.map((para) => {
                      const cf      = cardFb[para] ?? {};
                      const comment = cf.comment ?? '';
                      const minImp  = cf.minimal_improvement ?? null;
                      return (
                        <div key={para} className="rounded-lg border border-[#30363d] bg-[#161b22] px-3 py-2">
                          <div className="flex items-center justify-between mb-1 gap-2 flex-wrap">
                            <span className="text-[10px] font-mono font-semibold" style={{ color: violetHex }}>
                              {para}
                            </span>
                            <span className="text-[10px] font-mono text-[#8b949e]">
                              {phaseNames[para]}
                            </span>
                          </div>
                          {comment && <p className="text-[#8b949e] text-xs leading-relaxed">{comment}</p>}
                          {minImp && (
                            <p className="text-[#d29922] text-[10px] font-mono mt-1.5 leading-relaxed">✏ {minImp}</p>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Examiner note */}
              {feedback.examiner_note && (
                <div className="rounded-lg border border-[#30363d] bg-[#161b22] px-3 py-2.5">
                  <div className="text-[10px] font-mono leading-relaxed">
                    <span className="text-[#8b949e]">user@tutor:~$</span>{' '}
                    <span className="text-[#e6edf3]">examiner_note</span>
                    <br />
                    <span className="text-[#8b949e] mt-1 block italic">&gt; {feedback.examiner_note}</span>
                  </div>
                </div>
              )}

              <FeedbackFooter feedback={feedback} getToken={getToken} />
              <div className="pb-4" />
            </div>
          );
        })()}

        {/* ── ESSAY_20 FEEDBACK ───────────────────────────────────────────── */}
        {feedback && !isLoading && archetype === 'essay_20' && (() => {
          const roseHex    = '#f43f5e';
          const level      = feedback.level ?? feedback.awarded_level ?? 1;
          const total      = feedback.total ?? feedback.awarded_mark ?? 0;
          const cardFb     = feedback.card_feedback ?? {};
          const paragraphs = ['INTRODUCTION', 'PARAGRAPH 1', 'PARAGRAPH 2', 'PARAGRAPH 3', 'COUNTER-ARGUMENT', 'EVALUATIVE CONCLUSION'];

          const levelColors = {
            1: { border: 'border-[#f85149]/30', bg: 'bg-[#f85149]/5', text: 'text-[#f85149]' },
            2: { border: 'border-[#d29922]/30', bg: 'bg-[#d29922]/5', text: 'text-[#d29922]' },
            3: { border: 'border-[#58a6ff]/30', bg: 'bg-[#58a6ff]/5', text: 'text-[#58a6ff]' },
            4: { border: 'border-[#3fb950]/30', bg: 'bg-[#3fb950]/5', text: 'text-[#3fb950]' },
          };
          const lc = levelColors[level] ?? levelColors[1];

          const levelRanges = { 1: '1–5', 2: '6–10', 3: '11–15', 4: '16–20' };

          return (
            <div className="px-4 py-5 space-y-5">
              {/* Your Answer */}
              {steps && (() => {
                const cardLabels = ARCHETYPES.essay_20?.cards?.map((c) => c.label) ?? [];
                const answerText = cardLabels.map((lbl) => steps[lbl]?.trim()).filter(Boolean).join(' ');
                if (!answerText) return null;
                return (
                  <div>
                    <p className="text-[#8b949e] text-[10px] font-semibold uppercase tracking-wider mb-2">Your Answer</p>
                    <p className="text-[#e6edf3] text-xs leading-relaxed">{answerText}</p>
                    {timerElapsed > 0 && (() => {
                      const suggested = 20 * 60;
                      const pct  = Math.round((timerElapsed / suggested) * 100);
                      const mins = Math.floor(timerElapsed / 60);
                      const secs = timerElapsed % 60;
                      const col  = pct > 200 ? 'text-[#f85149]' : pct > 100 ? 'text-[#d29922]' : 'text-[#484f58]';
                      return (
                        <p className={`text-[10px] font-mono mt-2 ${col}`}>
                          ⏱ {mins}m {secs.toString().padStart(2, '0')}s — {pct}% of suggested exam time (20m 00s)
                        </p>
                      );
                    })()}
                    <div className="mt-4 border-t border-[#30363d]" />
                  </div>
                );
              })()}

              {/* Score dial + AO breakdown */}
              <div className="flex items-center gap-4">
                <div
                  className="flex items-center justify-center w-20 h-20 rounded-full border-4 score-reveal flex-shrink-0"
                  style={{ borderColor: roseHex, backgroundColor: 'rgba(244,63,94,0.10)' }}
                >
                  <div className="text-center">
                    <span className="text-2xl font-bold" style={{ color: roseHex }}>{total}</span>
                    <span className="text-[#8b949e] text-xs block">/20</span>
                  </div>
                </div>
                <div>
                  <div className="text-sm font-semibold" style={{ color: roseHex }}>
                    {total}/20 marks
                  </div>
                  <div className="text-[#8b949e] text-xs mt-0.5 font-mono">
                    AO1 {loadingBreakdown ? '…' : (feedback.ao1_marks ?? '?')}/10 &nbsp; AO2 {loadingBreakdown ? '…' : (feedback.ao2_marks ?? '?')}/10
                  </div>
                </div>
              </div>

              {/* Level badge */}
              <div className={`rounded-lg border ${lc.border} ${lc.bg} px-3 py-2.5`}>
                <div className={`text-xs font-bold font-mono tracking-widest ${lc.text}`}>
                  LEVEL {level} &nbsp;<span className="font-normal opacity-70">({levelRanges[level] ?? '?'} marks)</span>
                </div>
                {feedback.justification && (
                  <p className="text-[#8b949e] text-[10px] mt-1 leading-relaxed italic">
                    {feedback.justification}
                  </p>
                )}
              </div>

              {/* Per-paragraph breakdown */}
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <p className="text-[#8b949e] text-[10px] font-semibold uppercase tracking-wider">Paragraph Breakdown</p>
                  {loadingBreakdown && (
                    <div className="flex gap-0.5">
                      {[0, 1, 2].map((i) => (
                        <div
                          key={i}
                          className="w-1 h-1 rounded-full"
                          style={{ backgroundColor: roseHex, animation: `pulse 1.2s ease-in-out ${i * 0.2}s infinite` }}
                        />
                      ))}
                    </div>
                  )}
                </div>
                {loadingBreakdown ? (
                  <div className="space-y-2">
                    {paragraphs.map((para) => (
                      <div key={para} className="rounded-lg border border-[#30363d] bg-[#161b22] px-3 py-2">
                        <div className="flex items-center justify-between mb-1 gap-2">
                          <span className="text-[10px] font-mono font-semibold" style={{ color: roseHex }}>{para}</span>
                        </div>
                        <div className="h-2 w-3/4 bg-[#30363d] rounded animate-pulse mt-1" />
                      </div>
                    ))}
                  </div>
                ) : feedback.breakdownError ? (
                  <div className="text-[10px] font-mono text-[#f85149] bg-[#f85149]/5 border border-[#f85149]/20 rounded px-2.5 py-1.5">
                    ⚠ Paragraph analysis unavailable. Score above is still valid.
                  </div>
                ) : (
                  <div className="space-y-2">
                    {paragraphs.map((para) => {
                      const cf      = cardFb[para] ?? {};
                      const comment = cf.comment ?? '';
                      const minImp  = cf.minimal_improvement ?? null;
                      return (
                        <div key={para} className="rounded-lg border border-[#30363d] bg-[#161b22] px-3 py-2">
                          <div className="flex items-center justify-between mb-1 gap-2 flex-wrap">
                            <span className="text-[10px] font-mono font-semibold" style={{ color: roseHex }}>
                              {para}
                            </span>
                          </div>
                          {comment && <p className="text-[#8b949e] text-xs leading-relaxed">{comment}</p>}
                          {minImp && (
                            <p className="text-[#d29922] text-[10px] font-mono mt-1.5 leading-relaxed">✏ {minImp}</p>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Examiner note */}
              {feedback.examiner_note && (
                <div className="rounded-lg border border-[#30363d] bg-[#161b22] px-3 py-2.5">
                  <div className="text-[10px] font-mono leading-relaxed">
                    <span className="text-[#8b949e]">user@tutor:~$</span>{' '}
                    <span className="text-[#e6edf3]">examiner_note</span>
                    <br />
                    <span className="text-[#8b949e] mt-1 block italic">&gt; {feedback.examiner_note}</span>
                  </div>
                </div>
              )}

              <FeedbackFooter feedback={feedback} getToken={getToken} />
              <div className="pb-4" />
            </div>
          );
        })()}

        {/* ── 6-MARK / 9-MARK FEEDBACK ────────────────────────────────────── */}
        {feedback && !isLoading && archetype !== 'outline_4' && archetype !== 'assess_6' && archetype !== 'scaffold_9' && archetype !== 'essay_20' && (() => {
          const maxScore  = questionMarks ?? ARCHETYPES[archetype]?.marks ?? (markType === '9-mark' ? 9 : 6);
          const score     = feedback.awarded_mark ?? feedback.score ?? 0;
          const level     = feedback.awarded_level ?? null;
          const isLeveled = archetype === 'envsci_9' || archetype === 'envsci_25';
          const colors    = getScoreColor(score, maxScore);

          return (
            <div className="px-4 py-5 space-y-5">
              <div className="flex items-center gap-4">
                <ScoreDial score={score} max={maxScore} />
                <div>
                  <div className={`text-sm font-semibold ${colors.text}`}>
                    {feedback.scoreLabel ?? `${score}/${maxScore} marks`}
                  </div>
                  {isLeveled && level && (
                    <div className="text-[#8b949e] text-xs mt-0.5">Level {level}</div>
                  )}
                  {!isLeveled && feedback.band && (
                    <div className="text-[#8b949e] text-xs mt-0.5">{feedback.band}</div>
                  )}
                  {feedback.examinerNote && (
                    <p className="text-[#8b949e] text-xs mt-2 leading-relaxed italic">
                      "{feedback.examinerNote}"
                    </p>
                  )}
                </div>
              </div>

              {feedback.justification && (
                <div className="rounded-lg border border-[#30363d] bg-[#161b22] px-3 py-2.5">
                  <p className="text-[#8b949e] text-[10px] font-semibold uppercase tracking-wider mb-1.5">
                    Examiner Feedback
                  </p>
                  <p className="text-[#e6edf3] text-xs leading-relaxed">
                    {feedback.justification}
                  </p>
                </div>
              )}

              {feedback.aoBreakdown && (
                <div className="rounded-lg border border-[#30363d] bg-[#161b22] p-3 space-y-2">
                  <p className="text-[#8b949e] text-[10px] font-semibold uppercase tracking-wider mb-2">
                    Assessment Objectives
                  </p>
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-[#58a6ff] text-[10px] font-bold w-8">AO1</span>
                      <span className="text-[#e6edf3] text-xs leading-relaxed">{feedback.aoBreakdown.AO1}</span>
                    </div>
                    <div className="flex items-start gap-2">
                      <span className="text-[#3fb950] text-[10px] font-bold w-8">AO2</span>
                      <span className="text-[#e6edf3] text-xs leading-relaxed">{feedback.aoBreakdown.AO2}</span>
                    </div>
                  </div>
                </div>
              )}

              {feedback.caseStudyDetected?.length > 0 && (
                <div>
                  <p className="text-[#8b949e] text-[10px] font-semibold uppercase tracking-wider mb-2">
                    Case Studies Detected
                  </p>
                  <div className="flex flex-wrap gap-1.5">
                    {feedback.caseStudyDetected.map((cs, i) => (
                      <span key={i} className="text-[10px] px-2 py-0.5 rounded-full bg-[#3fb950]/10 text-[#3fb950] border border-[#3fb950]/20">
                        ✓ {cs}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {feedback.strengths?.length > 0 && (
                <div>
                  <p className="text-[#8b949e] text-[10px] font-semibold uppercase tracking-wider mb-2">
                    Strengths
                  </p>
                  <ul className="space-y-1.5">
                    {feedback.strengths.map((s, i) => (
                      <li key={i} className="flex items-start gap-2 text-xs text-[#e6edf3] leading-relaxed">
                        <span className="text-[#3fb950] mt-0.5 flex-shrink-0">▸</span>
                        {s}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {feedback.nextSteps?.length > 0 && (
                <div>
                  <p className="text-[#8b949e] text-[10px] font-semibold uppercase tracking-wider mb-2">
                    Next Steps
                  </p>
                  <ol className="space-y-2">
                    {feedback.nextSteps.map((step, i) => (
                      <li key={i} className="flex items-start gap-3 rounded-lg border border-[#30363d] bg-[#161b22] p-3">
                        <span className="text-[#58a6ff] text-xs font-bold flex-shrink-0 w-4">{i + 1}.</span>
                        <span className="text-[#e6edf3] text-xs leading-relaxed">{step}</span>
                      </li>
                    ))}
                  </ol>
                </div>
              )}

              <FeedbackFooter feedback={feedback} getToken={getToken} />

              <div className="pb-4" />
            </div>
          );
        })()}

      </div>

      )} {/* end peek ternary */}
    </aside>
  );
}
